import { FormEvent, useEffect, useState } from "react";
import styled from "styled-components";
import { format } from "date-fns";
import SellerService from "../../../services/seller.service";
import GeneralInput from "../../common/GeneralInput";
import { useGlobal } from "../../../context/global";
import { CashRegisterCheckpoint, ShiftPeriod } from "../../../types/common.types";
import CashMovementForm from "./Caja/CashMovementForm";

type Status = 'loading' | 'need_apertura' | 'need_cierre' | 'done' | 'blocked'

function currentPeriod(): 'AM' | 'PM' {
    return new Date().getHours() < 15 ? 'AM' : 'PM'
}

function extractMessage(error: any) {
    // Solo se muestran tal cual los errores esperados (400/403/404, con mensaje ya redactado para el
    // usuario en el backend). Un 500 es un error inesperado (ej. de base de datos) — no se le muestra
    // el detalle crudo a un vendedor, queda solo en consola para depurar.
    const status = error?.response?.status
    if (status && status < 500 && typeof error?.response?.data === 'string') return error.response.data
    return 'Ocurrió un error, intenta de nuevo'
}

export default function Caja() {
    const { setIsLoading } = useGlobal()
    const period = currentPeriod()
    const shiftDate = format(new Date(), 'yyyy-MM-dd')
    const [status, setStatus] = useState<Status>('loading')
    const [message, setMessage] = useState('')
    const [expectedAmount, setExpectedAmount] = useState(0)
    const [responsibleName, setResponsibleName] = useState('')
    const [countedAmount, setCountedAmount] = useState('')
    const [todayCheckpoints, setTodayCheckpoints] = useState<CashRegisterCheckpoint[]>([])

    async function load() {
        setIsLoading(true)
        try {
            const statusRes = await SellerService.getCheckpointStatus(shiftDate)
            const checkpoints: CashRegisterCheckpoint[] = statusRes.data
            setTodayCheckpoints(checkpoints)
            const apertura = checkpoints.find(c => c.period === period && c.checkpoint_type === 'apertura')
            const cierre = checkpoints.find(c => c.period === period && c.checkpoint_type === 'cierre')

            if (!apertura) {
                const preview = await SellerService.previewApertura(shiftDate, period)
                setExpectedAmount(preview.data.expected_amount)
                setResponsibleName(preview.data.responsible_seller?.name || '')
                setStatus('need_apertura')
            } else if (!cierre) {
                const preview = await SellerService.previewCierre(shiftDate, period)
                setExpectedAmount(preview.data.expected_amount)
                setStatus('need_cierre')
            } else {
                setStatus('done')
            }
        } catch (error: any) {
            setMessage(extractMessage(error))
            setStatus('blocked')
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    async function submit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault()
        setIsLoading(true)
        try {
            if (status === 'need_apertura') await SellerService.registerApertura(shiftDate, period, Number(countedAmount))
            else if (status === 'need_cierre') await SellerService.registerCierre(shiftDate, period, Number(countedAmount))
            setCountedAmount('')
            await load()
        } catch (error: any) {
            alert(extractMessage(error))
        } finally {
            setIsLoading(false)
        }
    }

    const difference = countedAmount !== '' ? Number(countedAmount) - expectedAmount : 0

    return (
        <Main>
            <h2>Caja — Turno {period === 'AM' ? 'mañana (10-15)' : 'tarde (15-21)'}</h2>
            <p className="date">{format(new Date(), 'dd/MM/yyyy')}</p>

            {/* Solo con la caja realmente abierta: apertura ya registrada, cierre todavía no. */}
            {status === 'need_cierre' && <CashMovementForm shiftDate={shiftDate} period={period} onMovementCreated={load} />}

            {status === 'loading' && <p className="hint">Cargando...</p>}

            {status === 'blocked' && (
                <div className="blocked">
                    <p>{message}</p>
                </div>
            )}

            {(status === 'need_apertura' || status === 'need_cierre') && (
                <form onSubmit={submit}>
                    <h3>{status === 'need_apertura' ? 'Abrir caja' : 'Cerrar caja'}</h3>
                    <p className="hint">
                        {status === 'need_apertura'
                            ? (responsibleName
                                ? `${responsibleName} contó $${expectedAmount.toLocaleString('es-CL')} al cerrar su turno. Por eso ahora se espera encontrar $${expectedAmount.toLocaleString('es-CL')} — cuenta el dinero real y anótalo.`
                                : `Aún no hay un cierre anterior registrado, así que se usa el saldo inicial de la cuenta: $${expectedAmount.toLocaleString('es-CL')}. Cuenta el dinero real y anótalo.`)
                            : `Con lo que abriste más los movimientos de hoy, se espera $${expectedAmount.toLocaleString('es-CL')}. Cuenta el dinero real y anótalo.`}
                    </p>
                    <GeneralInput
                        label="Cuenta el dinero y anota el total"
                        type="number"
                        required
                        value={countedAmount}
                        onChange={e => setCountedAmount(e.target.value)}
                        placeholder="0"
                    />
                    {countedAmount !== '' && Math.abs(difference) > 0.01 && (
                        <p className="warning">
                            {difference > 0 ? 'Sobran' : 'Faltan'} ${Math.abs(difference).toLocaleString('es-CL')} — quedará registrado como Desfase.
                        </p>
                    )}
                    <button type="submit">{status === 'need_apertura' ? 'Registrar apertura' : 'Registrar cierre'}</button>
                </form>
            )}

            {status === 'done' && (
                <p className="hint">Ya registraste apertura y cierre de este turno.</p>
            )}

            {todayCheckpoints.length > 0 && (
                <History>
                    <h3>Hoy</h3>
                    {todayCheckpoints.map(c => (
                        <div className="row" key={c.id}>
                            <span className="label">{c.period as ShiftPeriod} — {c.checkpoint_type}</span>
                            <span>esperado ${Number(c.expected_amount).toLocaleString('es-CL')}</span>
                            <span>contado ${Number(c.counted_amount).toLocaleString('es-CL')}</span>
                            {Math.abs(c.difference) > 0.01 && (
                                <span className="diff">{c.difference > 0 ? '+' : ''}{Number(c.difference).toLocaleString('es-CL')}</span>
                            )}
                        </div>
                    ))}
                </History>
            )}
        </Main>
    )
}

const Main = styled.main`
display: flex;
flex-direction: column;
gap: 15px;
max-width: 420px;
margin: 0 auto;
h2 {
    font-size: 20px;
    font-weight: 500;
}
.date {
    color: ${props => props.theme.gray};
    font-size: 13px;
}
.hint {
    font-size: 13px;
    color: ${props => props.theme.gray};
}
.warning {
    font-size: 13px;
    color: ${props => props.theme.lightRed};
    font-weight: 600;
}
.blocked {
    padding: 15px;
    border-radius: 8px;
    background-color: ${props => props.theme.whiteBone};
    color: ${props => props.theme.lightRed};
    font-size: 14px;
}
form {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 15px;
    border-radius: 10px;
    background-color: ${props => props.theme.whiteBone};
    h3 {
        font-size: 16px;
        font-weight: 500;
    }
    button {
        padding: 12px;
        border-radius: 8px;
        border: none;
        background-color: ${props => props.theme.blue};
        color: ${props => props.theme.white};
        font-weight: 600;
        cursor: pointer;
    }
}
`

const History = styled.div`
h3 {
    font-size: 15px;
    font-weight: 500;
    margin-bottom: 8px;
}
.row {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 8px 12px;
    border-radius: 8px;
    background-color: ${props => props.theme.whiteBone};
    margin-bottom: 6px;
    font-size: 13px;
    .label {
        font-weight: 600;
        text-transform: capitalize;
        flex: 1;
    }
    .diff {
        color: ${props => props.theme.lightRed};
        font-weight: 600;
    }
}
`
